import { useEffect, useRef, useState } from "react";

/** Below this much time left, the countdown switches to showing seconds. */
const FINAL_STRETCH_MS = 60 * 60 * 1000;
/** Tick cadence far from expiry — enough to notice we entered the final hour. */
const COARSE_TICK_MS = 60 * 1000;
/** Tick cadence inside the final hour, so the MM:SS clock actually moves. */
const FINE_TICK_MS = 1000;

/**
 * The expiry as a timestamp, or `null` when there is nothing usable to count down to — either absent, or a
 * string the browser cannot parse.
 *
 * Exported because "does this link have a usable expiry?" is asked in two places: here, to decide whether to
 * run a countdown, and by the page, to decide whether to offer the link at all. Answering it twice is how
 * they drift — one checking `Number.isFinite` and the other plain truthiness is enough to make the countdown
 * disappear while the link stays copyable and emailable.
 */
export const parseExpiry = (value: string | null | undefined): number | null => {
  const parsed = value ? new Date(value).getTime() : Number.NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

export interface LinkCountdownState {
  isExpired: boolean;
  msRemaining: number;
  /** "MM:SS", only inside the final hour. Null while expiry is still far off. */
  formattedTime: string | null;
}

/**
 * Counts down to a verification link's expiry.
 *
 * Built on the same three decisions as `useQuoteCountdown` — the only state is a ticking clock with
 * everything derived during render (storing the remaining time and writing it from an effect made a fresh
 * link flash as expired for one frame), a self-clearing interval, and a ref so `onExpire` fires once per
 * target. What it does not share is the cadence, and that is the whole reason it exists: quotes live for
 * minutes, so ticking every second and formatting MM:SS is right for them. A verification link lives for days
 * — the same hook would render "7199:59" and re-render ~432,000 times.
 *
 * So the cadence follows the stakes: once a minute while expiry is far away (where the display is an absolute
 * date and seconds are noise), once a second inside the final hour (where they are the point). Ticking is
 * also what re-renders the consumer, so keep this hook in a leaf component — in a page component it would
 * re-render the whole tree on every tick.
 *
 * @param expiresAt - ISO timestamp. Null/undefined disables the countdown.
 * @param onExpire - Called once per `expiresAt`, including when already past.
 */
export const useLinkCountdown = (
  expiresAt: string | null | undefined,
  onExpire?: () => void,
): LinkCountdownState => {
  const [now, setNow] = useState<number>(() => Date.now());
  const onExpireRef = useRef(onExpire);
  /** The `expiresAt` we already fired for, so a re-render cannot fire twice. */
  const firedForRef = useRef<string | null>(null);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  // An unparseable timestamp collapses to "no countdown", not to NaN. Raw,
  // `new Date("…").getTime()` yields NaN, which is not `null`, so every guard
  // below would silently take the wrong branch: `Math.max(0, NaN)` is NaN (not
  // 0), `NaN === 0` is false so nothing ever reads as expired, and
  // `Date.now() >= NaN` is false so the interval arms and never stops.
  const expirationTime = parseExpiry(expiresAt);
  const msRemaining = expirationTime === null ? 0 : Math.max(0, expirationTime - now);
  const isExpired = expirationTime !== null && msRemaining === 0;
  const isFinalStretch = msRemaining > 0 && msRemaining <= FINAL_STRETCH_MS;

  // Re-armed only when the target changes or when we cross into the final
  // stretch — `isFinalStretch` is a boolean, so a tick that does not flip it
  // does not tear the interval down.
  useEffect(() => {
    if (expirationTime === null || Date.now() >= expirationTime) return;

    const intervalId = window.setInterval(
      () => {
        const tick = Date.now();
        setNow(tick);
        if (tick >= expirationTime) window.clearInterval(intervalId);
      },
      isFinalStretch ? FINE_TICK_MS : COARSE_TICK_MS,
    );

    return () => window.clearInterval(intervalId);
  }, [expirationTime, isFinalStretch]);

  // Timers are throttled in background tabs and do not run at all while the
  // machine is suspended, so a tab left open across the expiry would come back
  // still showing the link as live. Re-read the clock whenever the user returns.
  useEffect(() => {
    if (expirationTime === null) return;

    const resync = () => setNow(Date.now());
    window.addEventListener("visibilitychange", resync);
    window.addEventListener("focus", resync);
    return () => {
      window.removeEventListener("visibilitychange", resync);
      window.removeEventListener("focus", resync);
    };
  }, [expirationTime]);

  useEffect(() => {
    if (!expiresAt || !isExpired || firedForRef.current === expiresAt) return;
    firedForRef.current = expiresAt;
    onExpireRef.current?.();
  }, [expiresAt, isExpired]);

  let formattedTime: string | null = null;
  if (isFinalStretch) {
    const totalSeconds = Math.floor(msRemaining / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    formattedTime = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }

  return { isExpired, msRemaining, formattedTime };
};
